import { useEffect, useCallback } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import * as api from './deliveryPlanningTemplatesApi';
import type { DeliveryPlanningTemplate } from './deliveryPlanningTemplatesApi';
import { useDetailView } from '../common/useDetailView';
import { sx14, FormRow, DetailViewShell } from '../common/detailView';

interface Props {
    deliveryPlanningTemplateId: number | null;
}

export function DeliveryPlanningTemplateView({ deliveryPlanningTemplateId }: Props) {
    const detail = useDetailView<DeliveryPlanningTemplate>();

    const load = useCallback(async () => {
        await detail.withLoading(async () => {
            if (deliveryPlanningTemplateId === null) {
                detail.setItem({ id: 0, deliveryPlanningTemplateName: '' });
                detail.setIsNew(true);
                detail.setIsEditing(true);
            } else {
                const data = await api.getDeliveryPlanningTemplate(deliveryPlanningTemplateId);
                detail.setItem(data);
                detail.setIsNew(false);
                detail.setIsEditing(false);
            }
        });
    }, [deliveryPlanningTemplateId]);

    useEffect(() => { load(); }, [load]);

    const save = async () => {
        await detail.withSaving(async () => {
            const response = detail.isNew
                ? await api.createDeliveryPlanningTemplate(detail.item!)
                : await api.updateDeliveryPlanningTemplate(detail.item!);
            detail.setItem(response);
            detail.setIsNew(false);
            detail.setIsEditing(false);
        });
    };

    return (
        <DetailViewShell<DeliveryPlanningTemplate>
            title={detail.item?.deliveryPlanningTemplateName ?? ''}
            isEditing={detail.isEditing}
            isNew={detail.isNew}
            saving={detail.saving}
            canSave={true}
            loading={detail.loading}
            error={detail.error}
            item={detail.item}
            onEdit={detail.startEdit}
            onSave={save}
            onCancel={detail.cancelEdit}
            onClearError={() => detail.setError(null)}
        >
            {(item) => (
                <>
                    <FormRow label="Mall benämning">
                        {!detail.isEditing ? (
                            <Typography sx={sx14}>{item.deliveryPlanningTemplateName}</Typography>
                        ) : (
                            <TextField size="small" fullWidth inputProps={{ maxLength: 100 }}
                                value={item.deliveryPlanningTemplateName ?? ''}
                                onChange={e => detail.update('deliveryPlanningTemplateName', e.target.value)} />
                        )}
                    </FormRow>
                </>
            )}
        </DetailViewShell>
    );
}
